import React from 'react';
import { useForm } from 'react-hook-form';
import FormField from './FormField';
import Button from './Button';
import { validateAbn, validateBsb, validateBankAccountNumber } from '../../utils/franchiseeFieldValidators';

const FranchiseeDetailsForm = ({ onSubmit, isSubmitting, onBack, onCancel }) => {
  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: {
      fseCode: '',
      franchiseName: '',
      firstName: '',
      lastName: '',
      phoneNumber: '',
      abn: '',
      gstRegistered: false,
      bankAccountName: '',
      bsbNumber: '',
      bankAccountNumber: '',
      territory: '',
      addressLine1: '',
      addressLine2: '',
      state: '',
      postalCode: '',
      country: 'Australia',
      regionalFranchisor: 'jims_mowing_geelong',
      regionalFranchisorOther: ''
    }
  });

  const regionalFranchisorValue = watch('regionalFranchisor');

  const stateOptions = [
    { value: 'NSW', label: 'New South Wales' },
    { value: 'VIC', label: 'Victoria' },
    { value: 'QLD', label: 'Queensland' },
    { value: 'WA', label: 'Western Australia' },
    { value: 'SA', label: 'South Australia' },
    { value: 'TAS', label: 'Tasmania' },
    { value: 'ACT', label: 'Australian Capital Territory' },
    { value: 'NT', label: 'Northern Territory' }
  ];

  const handleFormSubmit = (data) => {
    // Map regionalFranchisor value for backend
    const submissionData = {
      ...data,
      regionalFranchisor: data.regionalFranchisor === 'other' ? 'other' : 'jims_mowing_geelong',
      regionalFranchisorOther: data.regionalFranchisor === 'other' ? data.regionalFranchisorOther : ''
    };
    onSubmit(submissionData);
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-6">
      {/* Personal Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          Personal Information
        </h3>

        <div className="space-y-1">
          <label htmlFor="fseCode" className="block text-sm font-medium text-gray-700">
            FSE Code <span className="text-red-500">*</span>
          </label>
          <input
            id="fseCode"
            type="text"
            {...register('fseCode', { required: 'FSE Code is required' })}
            className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-colors duration-200 sm:text-sm ${
              errors.fseCode ? 'border-red-300' : 'border-gray-300'
            }`}
          />
          {errors.fseCode && (
            <p className="text-xs text-red-600 flex items-center">
              <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.fseCode.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="First Name"
            name="firstName"
            register={register}
            validation={{ required: "First name is required" }}
            error={errors.firstName}
            required
          />

          <FormField
            label="Last Name"
            name="lastName"
            register={register}
            validation={{ required: "Last name is required" }}
            error={errors.lastName}
            required
          />
        </div>

        <FormField
          label="Franchise Name"
          name="franchiseName"
          register={register}
          validation={{ required: "Franchise name is required" }}
          error={errors.franchiseName}
          placeholder="e.g., Jim's Mowing Melbourne East"
          required
        />
      </div>

      {/* Regional Franchisor */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          Regional Franchisor
        </h3>

        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">
            Who is your regional franchisor? <span className="text-red-500">*</span>
          </label>

          <div className="flex flex-col space-y-2">
            <label className="flex items-center">
              <input
                type="radio"
                value="jims_mowing_geelong"
                {...register('regionalFranchisor', { required: 'Please select your regional franchisor' })}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Jim's Mowing Geelong</span>
            </label>

            <label className="flex items-center">
              <input
                type="radio"
                value="other"
                {...register('regionalFranchisor', { required: 'Please select your regional franchisor' })}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Other</span>
            </label>
          </div>

          {errors.regionalFranchisor && (
            <p className="text-xs text-red-600 flex items-center">
              <svg className="h-3 w-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.regionalFranchisor.message}
            </p>
          )}

          {regionalFranchisorValue === 'other' && (
            <div className="ml-6">
              <FormField
                label="Please specify your regional franchisor"
                name="regionalFranchisorOther"
                register={register}
                validation={{ required: regionalFranchisorValue === 'other' ? 'Please specify your regional franchisor' : false }}
                error={errors.regionalFranchisorOther}
                placeholder="Enter your regional franchisor name"
                required
              />
            </div>
          )}
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          Contact Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Phone Number"
            name="phoneNumber"
            type="tel"
            register={register}
            validation={{ required: "Phone number is required" }}
            error={errors.phoneNumber}
            placeholder="0400 000 000"
            required
          />

          <FormField
            label="ABN"
            name="abn"
            register={register}
            validation={{ required: "ABN is required", validate: validateAbn }}
            error={errors.abn}
            placeholder="51 824 753 556"
            required
          />
        </div>

        <FormField
          label="GST Registered"
          name="gstRegistered"
          type="checkbox"
          register={register}
          error={errors.gstRegistered}
        />
      </div>

      {/* Banking Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          Banking Information
        </h3>

        <FormField
          label="Bank Account Name"
          name="bankAccountName"
          register={register}
          error={errors.bankAccountName}
          placeholder="Account holder name"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="BSB Number"
            name="bsbNumber"
            register={register}
            validation={{ validate: validateBsb }}
            error={errors.bsbNumber}
            placeholder="000-000"
          />

          <FormField
            label="Bank Account Number"
            name="bankAccountNumber"
            register={register}
            validation={{ validate: validateBankAccountNumber }}
            error={errors.bankAccountNumber}
            placeholder="123456789"
          />
        </div>
      </div>

      {/* Address Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900 border-b border-gray-200 pb-2">
          Address Information
        </h3>

        <FormField
          label="Territory"
          name="territory"
          register={register}
          error={errors.territory}
          placeholder="Service territory or area"
        />

        <FormField
          label="Address Line 1"
          name="addressLine1"
          register={register}
          error={errors.addressLine1}
          placeholder="Street address"
        />

        <FormField
          label="Address Line 2"
          name="addressLine2"
          register={register}
          error={errors.addressLine2}
          placeholder="Apartment, suite, etc. (optional)"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="State"
            name="state"
            type="select"
            register={register}
            error={errors.state}
            options={stateOptions}
            placeholder="Select state"
          />

          <FormField
            label="Postal Code"
            name="postalCode"
            register={register}
            error={errors.postalCode}
            placeholder="3000"
          />
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            onClick={onBack}
            className="flex-1 sm:w-auto"
          >
            Back
          </Button>

          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="flex-1 sm:w-auto"
            >
              Cancel
            </Button>
          )}
        </div>

        <Button
          type="submit"
          loading={isSubmitting}
          className="w-full sm:flex-1"
        >
          {isSubmitting ? 'Creating Account...' : 'Complete Registration'}
        </Button>
      </div>
    </form>
  );
};

export default FranchiseeDetailsForm;
